// src/pages/DynamicPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Container,
  Grid,
  Paper,
  Button,
  Link as MuiLink,
  Divider,
  Breadcrumbs,
  Chip
} from '@mui/material';
import { getDatabase, ref, get } from 'firebase/database';
import firebaseApp from '../adm/admin/firebaseConfig'; // ajuste o caminho

import DataTable from '../utils/Tabela';
import TitleSection from '../utils/TittleTheme';

import * as MuiIcons from '@mui/icons-material';
import {
  PictureAsPdf as PdfIcon,
  OpenInNew as ExternalLinkIcon,
  ChevronRight as ChevronRightIcon,
  Home as HomeIcon
} from '@mui/icons-material';

// Placeholder base64 local (sem request externo)
const FALLBACK_PLACEHOLDER =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGVkZWQiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1zaXplPSIzMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZmlsbD0iI2ZmZiI+SW1hZ2VtIGluZGlzcG9uw612ZWw8L3RleHQ+PC9zdmc+';

const DynamicPage = () => {
  const { pageName } = useParams();
  const navigate = useNavigate();

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const fetchPage = async () => {
      if (!pageName) {
        setError('Página não especificada.');
        setLoading(false);
        return;
      }

      try {
        const db = getDatabase(firebaseApp);
        const pagesRef = ref(db, 'pages');

        const snapshot = await get(pagesRef);
        if (snapshot.exists()) {
          const allPages = snapshot.val();
          const foundPage = Object.values(allPages).find(
            (page) => page.name === pageName
          );

          if (foundPage) {
            foundPage.elements = Array.isArray(foundPage.elements)
              ? foundPage.elements.map((el) => ({
                  ...el,
                  icon: el.icon || '',
                  iconPosition: el.iconPosition || 'start',
                  hasLink: el.hasLink || false,
                  linkUrl: el.linkUrl || '',
                  subItems: Array.isArray(el.subItems) ? el.subItems.map(sub => ({
                    ...sub,
                    icon: sub.icon || '',
                    iconPosition: sub.iconPosition || 'start',
                    hasLink: sub.hasLink || false,
                    linkUrl: sub.linkUrl || '',
                  })) : [],
                }))
              : [];
            setPageData(foundPage);
          } else {
            setError('Página não encontrada.');
          }
        } else {
          setError('Nenhuma página cadastrada.');
        }
      } catch (err) {
        console.error('Erro ao carregar página dinâmica:', err);
        setError('Não foi possível carregar o conteúdo solicitado.');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [pageName]);

  // Função auxiliar para renderizar ícones dinamicamente
  const renderIcon = (iconName, sxProps = {}) => {
    if (!iconName || !MuiIcons[iconName]) return null;
    const IconComponent = MuiIcons[iconName];
    return <IconComponent {...sxProps} />;
  };

  const renderAttachment = (attachmentUrl, attachmentType, label = 'Ver Anexo', size = 'medium') => {
    if (!attachmentUrl) return null;

    // Inferir tipo se não definido
    let type = attachmentType;
    if (!type && attachmentUrl.toLowerCase().endsWith('.pdf')) type = 'pdf';
    else if (!type) type = 'image';

    // Tamanhos consistentes com o backend
    const sizes = {
      small: { width: '40%', maxWidth: 400 },
      medium: { width: '70%', maxWidth: 700 },
      large: { width: '90%', maxWidth: 1000 },
      'full-width': { width: '100%' },
    };

    const imgStyle = sizes[size] || sizes['medium'];

    return (
      <Box sx={{ mt: 3, mb: 2 }}>
        {type === 'pdf' ? (
          <Button
            variant="contained"
            color="primary"
            href={attachmentUrl}
            target="_blank"
            startIcon={<PdfIcon />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
            }}
          >
            {label} (PDF)
          </Button>
        ) : (
          <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
            <img
              src={attachmentUrl}
              alt={label}
              loading="lazy"
              style={{
                ...imgStyle,
                height: 'auto',
                display: 'block',
                transition: 'transform 0.3s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onError={(e) => {
                e.target.src = FALLBACK_PLACEHOLDER;
                e.target.onerror = null;
              }}
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderElement = (element, index) => {
    const align = element.alignment || 'left';
    const IconComponent = element.icon ? MuiIcons[element.icon] : null;

    // Conteúdo com ou sem link
    const contentBody = element.type === 'text' ? (
      <Box
        dangerouslySetInnerHTML={{ __html: element.content }}
        sx={{
          lineHeight: 1.8,
          color: 'text.primary',
          '& p': { mb: 2 },
          '& a': { color: 'primary.main', fontWeight: 600 }
        }}
      />
    ) : (
      <Typography
        variant={element.type === 'title' ? 'h3' : element.type === 'subtitle' ? 'h5' : 'body1'}
        sx={{
          fontWeight: element.type === 'title' ? 800 : 500,
          color: element.type === 'subtitle' ? 'text.secondary' : 'text.primary',
          mb: 1,
          lineHeight: 1.3
        }}
      >
        {element.content}
      </Typography>
    );

    const mainContent = element.hasLink && element.linkUrl ? (
      <MuiLink
        href={element.linkUrl}
        target="_blank"
        sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          {contentBody} <ExternalLinkIcon fontSize="small" />
        </Box>
      </MuiLink>
    ) : contentBody;

    switch (element.type) {
      case 'title':
      case 'subtitle':
        return (
          <Box key={index} sx={{ textAlign: align, mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'center' ? 'center' : 'flex-start', gap: 2, mb: 2 }}>
              {IconComponent && element.iconPosition === 'start' && <IconComponent color="primary" sx={{ fontSize: 40 }} />}
              {mainContent}
              {IconComponent && element.iconPosition === 'end' && <IconComponent color="primary" sx={{ fontSize: 40 }} />}
            </Box>
            {renderAttachment(element.attachmentUrl, element.attachmentType, 'Documento de Apoio', element.size)}
            <Divider sx={{ width: '60px', height: '4px', bgcolor: 'primary.main', borderRadius: '2px', mx: align === 'center' ? 'auto' : 0 }} />
          </Box>
        );

      case 'text':
        return (
          <Box key={index} sx={{ textAlign: align, mb: 4, px: { md: 2 } }}>
            {mainContent}
            {renderAttachment(element.attachmentUrl, element.attachmentType, 'Anexo', element.size)}
          </Box>
        );

      case 'banner':
      case 'image': {
        const sizes = {
          small: { width: '40%', maxWidth: 400 },
          medium: { width: '70%', maxWidth: 700 },
          large: { width: '90%', maxWidth: 1000 },
          'full-width': { width: '100%' },
        };

        return (
          <Box key={index} sx={{ textAlign: align, my: 4 }}>
            <img
              src={element.content || FALLBACK_PLACEHOLDER}
              alt={element.content || 'Imagem'}
              loading="lazy"
              style={{
                ...sizes[element.size || 'medium'],
                height: 'auto',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                display: 'block',
                margin: '0 auto',
              }}
              onError={(e) => {
                e.target.src = FALLBACK_PLACEHOLDER;
                e.target.onerror = null;
              }}
            />
            {/* {renderAttachment(element.attachmentUrl, element.attachmentType, 'Anexo da Imagem', element.size)} */}
          </Box>
        );
      }

      case 'table':
        return (
          <Paper key={index} elevation={0} sx={{ p: 4, my: 6, borderRadius: '16px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <MuiIcons.TableChart color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{element.content || 'Tabela de Informações'}</Typography>
            </Box>
            {element.dataSource ? <DataTableWrapper dataSource={element.dataSource} /> : <Alert severity="warning">Configuração de dados pendente.</Alert>}
            {renderAttachment(element.attachmentUrl, element.attachmentType, 'Anexo da Tabela', element.size)}
          </Paper>
        );

      default:
        return null;
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', gap: 2 }}>
      <CircularProgress size={50} thickness={4} sx={{ color: 'primary.main' }} />
      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>A carregar portal...</Typography>
    </Box>
  );

  if (error || !pageData) return (
    <Container maxWidth="md" sx={{ py: 12, textAlign: 'center' }}>
      <Alert severity="error" variant="outlined" sx={{ mb: 4, borderRadius: '12px' }}>{error || 'Página não encontrada.'}</Alert>
      <Button variant="contained" startIcon={<HomeIcon />} onClick={() => navigate('/')} sx={{ borderRadius: '10px', px: 4 }}>Voltar ao Início</Button>
    </Container>
  );

  const sidebar = pageData.elements.find(el => el.type === 'sidebar');

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: 10 }}>
      <Box sx={{ bgcolor: 'rgba(0,0,0,0.02)', py: 3, mb: 6, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ mb: 2 }}>
            <MuiLink component="button" onClick={() => navigate('/')} sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', textDecoration: 'none' }}>
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" /> Início
            </MuiLink>
            <Typography color="text.primary" sx={{ fontWeight: 600 }}>{pageData.title}</Typography>
          </Breadcrumbs>
          <TitleSection title={pageData.title} subtitle={pageData.subtitle} />
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Grid container spacing={5}>
          <Grid item xs={12} md={sidebar ? 8 : 12}>
            {pageData.elements.filter(el => el.type !== 'sidebar').map((el, i) => renderElement(el, i))}
          </Grid>

          {sidebar && (
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: '20px', border: '1px solid', borderColor: 'divider', position: 'sticky', top: 100 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  {sidebar.icon && sidebar.iconPosition === 'start' && renderIcon(sidebar.icon, { color: 'primary', sx: { fontSize: 28 } })}
                  
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}
                  >
                    {sidebar.content || 'Navegação'}
                  </Typography>

                  {sidebar.icon && sidebar.iconPosition === 'end' && renderIcon(sidebar.icon, { color: 'primary', sx: { fontSize: 28 } })}
                </Box>

                <Box component="nav">
                  {sidebar.subItems?.map((item, idx) => {
                    const ItemIcon = item.icon ? MuiIcons[item.icon] : null;

                    const itemContent = item.type === 'text' ? (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.content}</Typography>
                    ) : item.type === 'link' ? (
                      <Button
                        fullWidth
                        href={item.content}
                        target="_blank"
                        endIcon={<ExternalLinkIcon fontSize="small" />}
                        sx={{ justifyContent: 'space-between', textTransform: 'none', color: 'text.primary', py: 1 }}
                      >
                        {item.content}
                      </Button>
                    ) : item.type === 'image' ? (
                      <img
                        src={item.content}
                        alt="Imagem sidebar"
                        loading="lazy"
                        style={{ width: '100%', borderRadius: '12px', marginBottom: 8 }}
                        onError={(e) => {
                          e.target.src = FALLBACK_PLACEHOLDER;
                          e.target.onerror = null;
                        }}
                      />
                    ) : null;

                    const wrappedContent = (item.type === 'text' && item.hasLink && item.linkUrl) ? (
                      <MuiLink
                        href={item.linkUrl}
                        target="_blank"
                        sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                      >
                        {itemContent}
                      </MuiLink>
                    ) : itemContent;

                    return (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, borderRadius: '8px', transition: '0.2s', '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' } }}>
                          {ItemIcon && item.iconPosition === 'start' && <ItemIcon sx={{ fontSize: 20, mr: 1, color: 'primary.light' }} />}
                          <ChevronRightIcon sx={{ fontSize: 16, mr: 1, color: 'primary.light' }} />
                          {wrappedContent}
                          {ItemIcon && item.iconPosition === 'end' && <ItemIcon sx={{ fontSize: 20, ml: 1, color: 'primary.light' }} />}
                        </Box>
                        {renderAttachment(item.attachmentUrl, item.attachmentType, 'Ver item', item.size || 'medium')}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

// Função auxiliar para renderizar ícones dinamicamente
const renderIcon = (iconName, props = {}) => {
  if (!iconName || !MuiIcons[iconName]) return null;
  const IconComponent = MuiIcons[iconName];
  return <IconComponent {...props} />;
};

// Wrapper de Tabela (mantido otimizado)
const DataTableWrapper = ({ dataSource }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await get(ref(getDatabase(firebaseApp), dataSource));
        if (snap.exists()) {
          const raw = snap.val();
          setData(Object.keys(raw).map(k => ({ id: k, ...raw[k], title: raw[k].nome || 'Documento' })));
        }
      } finally { setLoading(false); }
    };
    fetch();
  }, [dataSource]);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={30} /></Box>;
  return <DataTable data={data} />;
};

export default DynamicPage;